/**
 * Shared helpers for the "Add source" (Sources panel) and "Add Material" (Collection
 * Material) import dialogs. Both surfaces accept the same three input shapes — local
 * files, one-per-line URLs, and de-duplicated file batches — so the parsing/validation
 * logic lives here once instead of being duplicated per dialog.
 */

/** Strip a matching pair of wrapping " or ' (repeat for nested quotes). */
export function stripWrappingQuotes(value) {
    let v = String(value || '').trim();
    while (
        v.length >= 2
        && ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'")))
    ) {
        v = v.slice(1, -1).trim();
    }
    return v;
}

// One URL per line; ignores blank lines and trims whitespace / wrapping quotes.
export function parseUrlLines(raw) {
    return String(raw || '')
        .split(/\r?\n/)
        .map((line) => stripWrappingQuotes(line))
        .filter(Boolean);
}

// Prepends https:// to scheme-less input (e.g. "www.bbc.com/x") so users can paste a bare
// host+path. Protocol-relative ("//host") and explicit http(s) URLs are normalized too. The
// server probes https first and falls back to http, so defaulting to https here is safe.
export function normalizeUrlInput(value) {
    const v = stripWrappingQuotes(value);
    if (!v) return v;
    if (/^https?:\/\//i.test(v)) return v;
    if (v.startsWith('//')) return `https:${v}`;
    // Treat an explicit non-http scheme (e.g. "ftp:", "file:") as-is so it fails validation.
    const colon = v.indexOf(':');
    const slash = v.indexOf('/');
    const hasScheme = colon > 0 && (slash < 0 || colon < slash) && v.slice(0, colon).indexOf('.') < 0;
    if (hasScheme) return v;
    return `https://${v}`;
}

export function isLikelyUrl(value) {
    try {
        const parsed = new URL(normalizeUrlInput(value));
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch (_) {
        return false;
    }
}

export function dedupeFiles(files) {
    const seen = new Set();
    const result = [];
    for (const file of files) {
        const key = `${file.name}::${file.size}::${file.lastModified || 0}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(file);
    }
    return result;
}

// Distinguishes pasted HTML (e.g. copied from a webpage/rich editor) from plain text, so the
// Raw text dialogs (Sources and Collection Material) know whether to send `htmlContent` or
// `plainText` on submit.
export function looksLikeHtml(text) {
    return /<\/?[a-z][\s\S]*>/i.test(String(text || ''));
}
